/**
 * The inspector's generic form, generated from the domain's field schemas
 * (`FIELD_SCHEMAS`, `fieldSpecFor` — the same source ingress validates a
 * patch against, so the form accepts exactly what the server will):
 * one control per field of the kind, its widget chosen from the field's
 * schema, its value read through `readFields` (defaults filled in).
 *
 * Left out, because another part of the editor owns them: the structural
 * registers (`parentId`, `ord`, `deleted` — moves and deletes), text fields
 * (`script`, `notes` — Yjs, the script panel), open namespaces (`params` —
 * the typed piece form), and a piece's `pieceType` and `enable` (the typed
 * piece form's type picker and timing).
 */
import { FIELD_SCHEMAS, fieldSpecFor, OPEN_NAMESPACES, readFields, type RowKind, TEXT_FIELDS } from '@acp/domain/fields.js';
import { jsonValueSchema, type JsonValue } from '@acp/domain/primitives.js';
import { z } from 'zod';
import { formatDuration, parseDuration } from './duration.js';

export type ControlType = 'boolean' | 'duration' | 'text' | 'enum' | 'json';

export type FieldControl = {
  readonly path: string;
  readonly label: string;
  readonly type: ControlType;
  readonly nullable: boolean;
  readonly options: readonly string[];
  /** The value as the control edits it: a boolean for a checkbox, else the text in the input. */
  readonly value: boolean | string;
};

const STRUCTURAL = new Set(['parentId', 'ord', 'deleted']);
const OWNED_BY_PIECE_FORM = new Set(['pieceType', 'enable']);

/** `expectedDuration` → `Expected duration`. */
export function labelFor(path: string): string {
  const words = path.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

type Shape = { readonly type: ControlType; readonly nullable: boolean; readonly options: readonly string[] };

function shapeOf(schema: z.core.$ZodType): Shape {
  if (schema instanceof z.ZodNullable) return { ...shapeOf(schema.unwrap()), nullable: true };
  if (schema instanceof z.ZodBoolean) return { type: 'boolean', nullable: false, options: [] };
  // Every number field of the tree's kinds is a duration in ms (`durationMsSchema`).
  if (schema instanceof z.ZodNumber) return { type: 'duration', nullable: false, options: [] };
  if (schema instanceof z.ZodString) return { type: 'text', nullable: false, options: [] };
  if (schema instanceof z.ZodEnum) return { type: 'enum', nullable: false, options: schema.options.map(String) };
  return { type: 'json', nullable: false, options: [] };
}

function shown(kind: RowKind, path: string): boolean {
  if (STRUCTURAL.has(path) || TEXT_FIELDS[kind].includes(path) || OPEN_NAMESPACES[kind].includes(path)) return false;
  return !((kind === 'piece' || kind === 'adlib') && OWNED_BY_PIECE_FORM.has(path));
}

function display(shape: Shape, value: unknown): boolean | string {
  switch (shape.type) {
    case 'boolean':
      return value === true;
    case 'duration':
      return typeof value === 'number' ? formatDuration(value) : '';
    case 'text':
    case 'enum':
      return typeof value === 'string' ? value : '';
    case 'json':
      return value === undefined || value === null ? '' : JSON.stringify(value);
  }
}

/** The controls for a row of `kind` with fields `f`, in schema order. */
export function fieldControls(kind: RowKind, f: Readonly<Record<string, unknown>>): readonly FieldControl[] {
  const read = readFields(kind, f);
  const values: Readonly<Record<string, unknown>> = read.ok ? read.value.fields : f;
  const out: FieldControl[] = [];
  for (const path of Object.keys(FIELD_SCHEMAS[kind].shape)) {
    if (!shown(kind, path)) continue;
    const spec = fieldSpecFor(kind, path);
    if (spec.type !== 'value') continue;
    const shape = shapeOf(spec.schema);
    out.push({ path, label: labelFor(path), ...shape, value: display(shape, values[path]) });
  }
  return out;
}

export type ParsedInput = { readonly ok: true; readonly value: JsonValue } | { readonly ok: false; readonly error: string };

function toValue(type: ControlType, nullable: boolean, raw: boolean | string): ParsedInput {
  if (typeof raw === 'boolean') return { ok: true, value: raw };
  if (type === 'duration') {
    const ms = parseDuration(raw);
    if (ms === undefined) return { ok: false, error: 'not a duration (m:ss)' };
    return { ok: true, value: ms };
  }
  if (type === 'json') {
    if (raw.trim() === '' && nullable) return { ok: true, value: null };
    try {
      const parsed = jsonValueSchema.safeParse(JSON.parse(raw));
      return parsed.success ? { ok: true, value: parsed.data } : { ok: false, error: 'not JSON' };
    } catch {
      return { ok: false, error: 'not JSON' };
    }
  }
  if (raw === '' && nullable) return { ok: true, value: null };
  return { ok: true, value: raw };
}

/**
 * What a control's input means as a field value, checked against the field's
 * own schema — so an input the server would refuse is reported here, next to
 * the field, instead of as a nack.
 */
export function parseFieldInput(kind: RowKind, control: Pick<FieldControl, 'path' | 'type' | 'nullable'>, raw: boolean | string): ParsedInput {
  const spec = fieldSpecFor(kind, control.path);
  const value = toValue(control.type, control.nullable, raw);
  if (!value.ok || spec.type !== 'value') return value;
  const checked = z.safeParse(spec.schema, value.value);
  if (checked.success) return value;
  return { ok: false, error: checked.error.issues[0]?.message ?? 'invalid' };
}
